let worker = null;
let isModelReady = false;

export async function initializeModel() {
    if (isModelReady) {
        // Even if model is ready, dispatch a 100% progress event to ensure UI is in sync
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('modelLoadingProgress', { 
                detail: { status: 'ready', progress: 100 }
            }));
        }
        return Promise.resolve();
    }

    if (worker) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Model initialization timeout'));
            }, 60000);

            let lastProgress = 0;
            const handler = (event) => {
                if (event.data.status === 'ready') {
                    isModelReady = true;
                    clearTimeout(timeout);
                    worker.removeEventListener('message', handler);
                    
                    // Send final 100% progress only if we haven't recently
                    if (lastProgress < 100) {
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('modelLoadingProgress', { 
                                detail: { status: 'ready', progress: 100 }
                            }));
                        }
                    }
                    
                    resolve();
                } else if (event.data.status === 'error') {
                    clearTimeout(timeout);
                    worker.removeEventListener('message', handler);
                    reject(new Error(event.data.error));
                } else if (event.data.status === 'loading') {
                    // Forward all progress updates to the main thread
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('modelLoadingProgress', { 
                            detail: {
                                status: event.data.status,
                                progress: event.data.progress || 0,
                                message: event.data.message
                            }
                        }));
                    }
                }
            };

            worker.addEventListener('message', handler);
            worker.postMessage({ text: 'init' });
        });
    }

    // Create worker
    worker = new Worker(new URL('./sentimentWorker.js', import.meta.url), { 
        type: 'module' 
    });

    return initializeModel(); // Recursive call now that worker exists
}

export async function analyzeSentiment(text) {
    if (!text?.trim()) {
        return { sentiment: 'neutral', confidence: 50, error: 'Empty input' };
    }

    try {
        if (!isModelReady) {
            await initializeModel();
        }

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Analysis timeout'));
            }, 15000);

            const handler = (event) => {
                if (event.data.status === 'complete') {
                    clearTimeout(timeout);
                    worker.removeEventListener('message', handler);
                    
                    const result = event.data.output;
                    if (!Array.isArray(result) || !result[0]) {
                        reject(new Error('Invalid response'));
                        return;
                    }

                    const { label, score } = result[0];
                    const sentimentMap = {
                        'POSITIVE': 'positive',
                        'NEGATIVE': 'negative',
                        'NEUTRAL': 'neutral'
                    };

                    resolve({
                        sentiment: sentimentMap[label.toUpperCase()] || label.toLowerCase(),
                        confidence: Math.round(score * 100),
                        text: text
                    });
                } else if (event.data.status === 'error') {
                    clearTimeout(timeout);
                    worker.removeEventListener('message', handler);
                    reject(new Error(event.data.error));
                } else if (event.data.status === 'loading') {
                    // Forward progress updates during analysis (though this is rare)
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('modelLoadingProgress', { 
                            detail: {
                                status: event.data.status,
                                progress: event.data.progress || 0,
                                message: event.data.message
                            }
                        }));
                    }
                }
            };

            worker.addEventListener('message', handler);
            worker.postMessage({ text });
        });

    } catch (error) {
        console.error('Sentiment analysis error:', error);
        return {
            sentiment: 'error',
            confidence: 0,
            error: error.message,
            text: text
        };
    }
}

export function isModelInitialized() {
    return isModelReady;
}

export function cleanup() {
    if (worker) {
        worker.terminate();
        worker = null;
    }
    isModelReady = false;
}

if (typeof window !== 'undefined') {
    window.analyzeSentiment = analyzeSentiment;
    window.initializeModel = initializeModel;
    window.isModelInitialized = isModelInitialized;
}